import {
  SunIcon,
  MoonIcon,
  PhoneIcon,
  ClockIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { useState, ChangeEvent, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Fieldset, FieldGroup } from '../../../components/fieldset';
import { Heading } from '../../../components/heading';
import { Text } from '../../../components/text';
import { type Step } from './StepNavigation';
import { RatesFormData } from './types';
import { BrandLegend, ValidationMessage } from './utils';

// Map shift types to icons
export const SHIFT_TYPES = {
  DAY: {
    key: 'DAY',
    apiKey: 'shift_rate_day',
    formKey: 'shiftRateDay',
    label: 'Day',
    icon: SunIcon,
  },
  NIGHT: {
    key: 'NIGHT',
    apiKey: 'shift_rate_night',
    formKey: 'shiftRateNight',
    label: 'Night',
    icon: MoonIcon,
  },
  CALL: {
    key: 'CALL',
    apiKey: 'shift_rate_call',
    formKey: 'shiftRateCall',
    label: 'Call',
    icon: PhoneIcon,
  },
  '24H': {
    key: '24H',
    apiKey: 'shift_rate_24h',
    formKey: 'shiftRate24h',
    label: '24 Hour',
    icon: ClockIcon,
  },
} as const;

type ShiftOptionProps = {
  icon: React.ElementType;
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  selected: boolean;
};

const ShiftOption = ({ icon: Icon, label, value, onChange, selected }: ShiftOptionProps) => {
  return (
    <label
      className={`tw:!flex tw:flex-col tw:gap-2 tw:cursor-pointer tw:flex-1 tw:items-center tw:border tw:rounded-md tw:px-2 tw:py-2 tw:transition-colors tw:ease-in-out tw:duration-100
        ${
          selected
            ? 'tw:border-brand-green-1 tw:bg-brand-green-1/10 tw:text-brand-green-1 tw:dark:border-none tw:dark:text-zinc-300 tw:dark:bg-brand-green-1'
            : 'tw:border-gray-300 tw:bg-white tw:hover:border-gray-400 tw:dark:border-zinc-300 tw:dark:bg-zinc-500 tw:dark:text-zinc-300 tw:dark:border-none'
        }`}
    >
      <Icon
        className={`tw:w-6 tw:h-6 ${
          selected
            ? 'tw:text-brand-green-1 tw:dark:text-zinc-300'
            : 'tw:text-gray-500 tw:dark:text-zinc-300'
        }`}
      />
      <span className="tw:text-center tw:text-xs">{label}</span>
      <input
        type="checkbox"
        value={value}
        className="tw:hidden"
        onChange={onChange}
        checked={selected}
      />
    </label>
  );
};

type ShiftRateInputProps = {
  label: string;
  value: string;
  onRateChange: (shiftType: string, rate: string) => void;
  rateValue: string;
};

const ShiftRateInput = ({ label, value, onRateChange, rateValue }: ShiftRateInputProps) => {
  const handleRateChange = (e) => {
    onRateChange(value, e.target.value);
  };

  return (
    <div className="tw:relative tw:mb-4">
      <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:dark:text-zinc-300">
        {label} Rate
      </label>
      <div className="tw:mt-1 tw:relative">
        <div className="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-gray-500 tw:dark:text-zinc-300">
          <CurrencyDollarIcon className="tw:w-5 tw:h-5" />
        </div>

        <input
          type="number"
          placeholder={`Enter ${label} Rate`}
          value={rateValue || ''}
          onChange={handleRateChange}
          className="tw:pl-10 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:shadow-sm tw:focus:outline-none tw:focus:ring-1 tw:focus:ring-brand-green-0 tw:focus:border-brand-green-0 tw:w-full tw:dark:border-white/10 tw:dark:bg-white/5 tw:dark:text-white"
        />
        <span className="tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-gray-500 tw:dark:text-zinc-300 tw:text-sm">
          /hr
        </span>
      </div>
    </div>
  );
};

interface Option {
  value: string;
  label: string;
}

type ShiftFrequencyOptionProps = {
  label: string;
  value: string;
  selected: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const ShiftFrequencyOption = ({ label, value, selected, onChange }: ShiftFrequencyOptionProps) => {
  return (
    <label
      className={`tw:!flex tw:items-center tw:justify-between tw:cursor-pointer tw:p-3 tw:border tw:rounded-md tw:transition-colors
        ${
          selected
            ? 'tw:border-brand-green-1 tw:bg-brand-green-1/10 tw:text-brand-green-1 tw:dark:border-none tw:dark:text-zinc-300 tw:dark:bg-brand-green-1'
            : 'tw:border-gray-300 tw:bg-white tw:hover:border-gray-400 tw:dark:border-zinc-300 tw:dark:bg-zinc-500 tw:dark:text-zinc-300 tw:dark:border-none'
        }`}
    >
      <div>
        <div className="tw:font-medium">{value}</div>
        <div
          className={`tw:text-sm ${
            selected
              ? 'tw:text-brand-green-1 tw:dark:text-zinc-300'
              : 'tw:text-gray-600 tw:dark:text-zinc-300'
          }`}
        >
          {label}
        </div>
      </div>
      <input
        type="radio"
        name="shiftFrequency"
        value={value}
        checked={selected}
        onChange={onChange}
        className="tw:flex-shrink-0 tw:w-5 tw:h-5 tw:appearance-none tw:rounded-full tw:border-1 tw:border-gray-300 tw:checked:border-brand-green-1 tw:checked:bg-white tw:relative tw:before:content-[''] tw:before:block tw:before:w-3 tw:before:h-3 tw:before:rounded-full tw:before:absolute tw:before:top-1/2 tw:before:left-1/2 tw:before:-translate-x-1/2 tw:before:-translate-y-1/2 tw:checked:before:bg-brand-green-1 tw:transition-all tw:dark:checked:bg-zinc-300"
      />
    </label>
  );
};

type TravelLengthOptionProps = {
  label: string;
  value: string;
  selected: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const TravelLengthOption = ({ label, value, selected, onChange }: TravelLengthOptionProps) => {
  return (
    <label
      className={`tw:!flex tw:items-center tw:justify-between tw:cursor-pointer tw:p-3 tw:border tw:rounded-md tw:transition-colors
        ${
          selected
            ? 'tw:border-brand-green-1 tw:bg-brand-green-1/10 tw:text-brand-green-1 tw:dark:border-none tw:dark:text-zinc-300 tw:dark:bg-brand-green-1'
            : 'tw:border-gray-300 tw:bg-white tw:hover:border-gray-400 tw:dark:border-zinc-300 tw:dark:bg-zinc-500 tw:dark:text-zinc-300 tw:dark:border-none'
        }`}
    >
      <div>
        <div className="tw:font-medium">{value}</div>
        <div
          className={`tw:text-sm ${
            selected
              ? 'tw:text-brand-green-1 tw:dark:text-zinc-300'
              : 'tw:text-gray-600 tw:dark:text-zinc-300'
          }`}
        >
          {label}
        </div>
      </div>
      <input
        type="radio"
        name="travelLength"
        value={value}
        checked={selected}
        onChange={onChange}
        className="tw:flex-shrink-0 tw:w-5 tw:h-5 tw:appearance-none tw:rounded-full tw:border-1 tw:border-gray-300 tw:checked:border-brand-green-1 tw:checked:bg-white tw:relative tw:before:content-[''] tw:before:block tw:before:w-3 tw:before:h-3 tw:before:rounded-full tw:before:absolute tw:before:top-1/2 tw:before:left-1/2 tw:before:-translate-x-1/2 tw:before:-translate-y-1/2 tw:checked:before:bg-brand-green-1 tw:transition-all tw:dark:checked:bg-zinc-300"
      />
    </label>
  );
};

interface RatesProps {
  stepInfo: Step;
  initialData?: RatesFormData;
  registrationSteps?: string[];
  providerRateOptions: Option[];
  workTypeOptions: Option[];
  travelTypeOptions: Option[];
}

export interface RatesRef {
  formData: RatesFormData;
}

export const Rates = forwardRef<RatesRef, RatesProps>(
  ({ stepInfo, initialData, registrationSteps, workTypeOptions, travelTypeOptions }, ref) => {
    const [formData, setFormData] = useState<RatesFormData>(
      initialData || {
        shiftRateDay: null,
        shiftRateNight: null,
        shiftRateCall: null,
        shiftRate24h: null,
        travelType: '',
        workType: '',
      }
    );
    const [selectedShiftTypes, setSelectedShiftTypes] = useState<Array<keyof typeof SHIFT_TYPES>>(
      []
    );
    const [validationMessages, setValidationMessages] = useState<
      Record<string, { message: string; colorClass: string }>
    >({});

    useImperativeHandle(ref, () => ({
      formData,
    }));

    useEffect(() => {
      if (initialData) {
        setFormData(initialData);
        const types: Array<keyof typeof SHIFT_TYPES> = [];
        if (initialData.shiftRateDay !== null) types.push('DAY');
        if (initialData.shiftRateNight !== null) types.push('NIGHT');
        if (initialData.shiftRateCall !== null) types.push('CALL');
        if (initialData.shiftRate24h !== null) types.push('24H');
        setSelectedShiftTypes(types);
      }
    }, [initialData]);

    useEffect(() => {
      if (!registrationSteps?.includes('RATES')) return;

      const newValidationMessages: Record<string, { message: string; colorClass: string }> = {};

      const hasAnyRate = [
        formData.shiftRateDay,
        formData.shiftRateNight,
        formData.shiftRateCall,
        formData.shiftRate24h,
      ].some((rate) => rate !== null && rate !== undefined);

      const ratesValidationWarningColorClass = 'tw:text-stoplight-warning';

      if (!hasAnyRate) {
        newValidationMessages.shiftRates = {
          message: 'Please select a shift type and then enter a rate',
          colorClass: ratesValidationWarningColorClass,
        };
      }

      if (!formData.workType) {
        newValidationMessages.workType = {
          message: 'Please select how many shifts you are open to working',
          colorClass: ratesValidationWarningColorClass,
        };
      }

      if (!formData.travelType) {
        newValidationMessages.travelType = {
          message: 'Please select where you are looking to work',
          colorClass: ratesValidationWarningColorClass,
        };
      }

      setValidationMessages(newValidationMessages);
    }, [formData, registrationSteps]);

    const handleShiftTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value as keyof typeof SHIFT_TYPES;
      setSelectedShiftTypes((prev) =>
        e.target.checked ? [...prev, value] : prev.filter((type) => type !== value)
      );

      if (!e.target.checked) {
        const shiftType = SHIFT_TYPES[value];
        setFormData((prev) => ({
          ...prev,
          [shiftType.formKey]: null,
        }));
      }
    };

    const handleRateChange = (shiftType: string, rate: string) => {
      const shift = SHIFT_TYPES[shiftType as keyof typeof SHIFT_TYPES];
      setFormData((prev) => ({
        ...prev,
        [shift.formKey]: rate === '' ? null : parseFloat(rate),
      }));
    };

    const handleWorkTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        workType: e.target.value,
      }));
    };

    const handleTravelTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        travelType: e.target.value,
      }));
    };

    return (
      <div className={`tw:flex tw:flex-col tw:p-4`}>
        <div className="tw:border-b tw:border-zinc-200 tw:mb-4">
          <Heading className="tw:!mb-0 tw:dark:!text-zinc-300">{stepInfo.label}</Heading>
          <Text className="tw:pb-2 tw:!mb-0">{stepInfo.description}</Text>
        </div>

        <form id="ratesForm">
          <Fieldset className="tw:!mb-6">
            <BrandLegend>What types of shifts are you open to working?</BrandLegend>
            <ValidationMessage
              message={validationMessages.shiftRates?.message}
              colorClass={validationMessages.shiftRates?.colorClass}
            />
            <FieldGroup>
              <div className="tw:flex tw:gap-2 tw:sm:gap-4 tw:mb-6">
                {Object.values(SHIFT_TYPES).map((shift) => (
                  <ShiftOption
                    key={shift.key}
                    icon={shift.icon}
                    label={shift.label}
                    value={shift.key}
                    selected={selectedShiftTypes.includes(shift.key)}
                    onChange={handleShiftTypeChange}
                  />
                ))}
              </div>

              {selectedShiftTypes.map((shiftType) => {
                const shift = SHIFT_TYPES[shiftType];
                const rateValue = formData[shift.formKey];

                return (
                  <ShiftRateInput
                    key={shift.key}
                    label={shift.label}
                    value={shift.key}
                    onRateChange={handleRateChange}
                    rateValue={rateValue?.toString() ?? ''}
                  />
                );
              })}
            </FieldGroup>
          </Fieldset>

          <Fieldset className="tw:!mb-6">
            <BrandLegend>How many shifts are you open to working?</BrandLegend>
            <ValidationMessage
              message={validationMessages.workType?.message}
              colorClass={validationMessages.workType?.colorClass}
            />
            <FieldGroup>
              <div className="tw:flex tw:flex-col tw:gap-4">
                {workTypeOptions.map((option) => (
                  <ShiftFrequencyOption
                    key={option.value}
                    label={option.label}
                    value={option.value}
                    selected={formData.workType === option.value}
                    onChange={handleWorkTypeChange}
                  />
                ))}
              </div>
            </FieldGroup>
          </Fieldset>

          <Fieldset className="tw:!mb-6">
            <BrandLegend>Where are you looking to work?</BrandLegend>
            <ValidationMessage
              message={validationMessages.travelType?.message}
              colorClass={validationMessages.travelType?.colorClass}
            />
            <FieldGroup>
              <div className="tw:flex tw:flex-col tw:gap-4">
                {travelTypeOptions.map((option) => (
                  <TravelLengthOption
                    key={option.value}
                    label={option.label}
                    value={option.value}
                    selected={formData.travelType === option.value}
                    onChange={handleTravelTypeChange}
                  />
                ))}
              </div>
            </FieldGroup>
          </Fieldset>

          <div className="tw:flex tw:justify-end">
            <button type="submit" className="tw:hidden">
              Submit
            </button>
          </div>
        </form>
      </div>
    );
  }
);
Rates.displayName = 'Rates';
